import React, { useEffect, useRef } from 'react';
import { Spin } from 'antd';
import { SupportTicketDetail, SupportUploadLimits } from '../../services/supportApi';
import AttachmentView from './AttachmentView';
import Composer from './Composer';
import { formatMessageTime } from './format';
import * as S from './styles';

interface Props {
  ticket: SupportTicketDetail | null;
  loading: boolean;
  limits: SupportUploadLimits;
  onSend: (body: string, files: File[], onProgress: (p: number) => void) => Promise<unknown>;
}

const TicketThread: React.FC<Props> = ({ ticket, loading, limits, onSend }) => {
  const bottomRef = useRef<HTMLDivElement>(null);
  const count = ticket?.messages.length ?? 0;

  useEffect(() => {
    // Optional call: jsdom has no scrollIntoView.
    bottomRef.current?.scrollIntoView?.({ block: 'end' });
  }, [count, ticket?.id]);

  if (loading || !ticket) {
    return (
      <S.Body>
        <S.Empty>
          <Spin />
        </S.Empty>
      </S.Body>
    );
  }

  const closed = ticket.status === 'CLOSED';

  return (
    <>
      <S.Body>
        <S.MessageList>
          {ticket.messages.map((m) => {
            if (m.isSystem) {
              return (
                <S.SystemLine key={m.id}>
                  {m.body} · {formatMessageTime(m.createdAt)}
                </S.SystemLine>
              );
            }
            const mine = m.senderRole === 'USER';
            return (
              <S.MessageRow key={m.id} $mine={mine}>
                <S.Bubble $mine={mine}>
                  {m.body}
                  <AttachmentView attachments={m.attachments} />
                </S.Bubble>
                <S.BubbleMeta>
                  {mine ? 'You' : m.senderName || 'Support'} · {formatMessageTime(m.createdAt)}
                </S.BubbleMeta>
              </S.MessageRow>
            );
          })}
          <div ref={bottomRef} />
        </S.MessageList>
      </S.Body>
      <S.Footer>
        {closed ? (
          <S.ClosedNote>This ticket is closed. Open a new report if the problem is back.</S.ClosedNote>
        ) : (
          <Composer
            limits={limits}
            onSend={onSend}
            placeholder={
              ticket.status === 'RESOLVED' ? 'Reply to reopen this ticket…' : 'Type a message…'
            }
          />
        )}
      </S.Footer>
    </>
  );
};

export default TicketThread;
